import * as React from 'react';

import { SignedNumber } from '@dndbeyond/character-common-components/es';
import { Ability, AbilityUtils, HelperUtils, RuleData, RuleDataUtils } from '@dndbeyond/character-rules-engine/es';

import AbilityIcon from '../Icons/AbilityIcon/AbilityIcon';

interface Props {
    ability: Ability;
    maxBonusScore: number;
    minBonusScore: number;
    ruleData: RuleData;
    onOtherBonusChange?: (abilityId: number, value: number | null) => void;
    onOverrideScoreChange?: (abilityId: number, value: number | null) => void;
    showHeader: boolean;
    isReadonly: boolean;
    className: string;
}

interface State {
    overrideScore: number | null;
    otherBonus: number | null;
}

export default class AbilityScoreManager extends React.PureComponent<Props, State> {
    static defaultProps = {
        minBonusScore: -10,
        maxBonusScore: 10,
        showHeader: true,
        className: '',
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            overrideScore: AbilityUtils.getOverrideScore(props.ability),
            otherBonus: AbilityUtils.getOtherBonus(props.ability),
        };
    }

    handleOtherBonusBlur = (evt: React.FocusEvent<HTMLInputElement>): void => {
        const { ability, onOtherBonusChange, minBonusScore, maxBonusScore } = this.props;

        if (onOtherBonusChange) {
            let parsedValue: number | null = HelperUtils.parseInputInt(evt.target.value);
            if (parsedValue !== null) {
                parsedValue = HelperUtils.clampInt(parsedValue, minBonusScore, maxBonusScore);
            }
            onOtherBonusChange(AbilityUtils.getId(ability), parsedValue);

            this.setState({
                otherBonus: parsedValue,
            });
        }
    };

    handleOverrideScoreBlur = (evt: React.FocusEvent<HTMLInputElement>): void => {
        const { ability, onOverrideScoreChange, ruleData } = this.props;

        if (onOverrideScoreChange) {
            const minStatScore = RuleDataUtils.getMinStatScore(ruleData);
            const maxStatScore = RuleDataUtils.getMaxStatScore(ruleData);

            let parsedValue: number | null = HelperUtils.parseInputInt(evt.target.value);
            if (parsedValue !== null) {
                parsedValue = HelperUtils.clampInt(parsedValue, minStatScore, maxStatScore);
            }

            onOverrideScoreChange(AbilityUtils.getId(ability), parsedValue);

            this.setState({
                overrideScore: parsedValue,
            });
        }
    };

    handleOtherBonusChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            otherBonus: HelperUtils.parseInputInt(evt.target.value),
        });
    };

    handleOverrideScoreChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            overrideScore: HelperUtils.parseInputInt(evt.target.value),
        });
    };

    render() {
        const { otherBonus, overrideScore } = this.state;
        const { ability, showHeader, isReadonly, className } = this.props;

        const label = AbilityUtils.getLabel(ability);
        const modifier = AbilityUtils.getModifier(ability);
        const baseScore = AbilityUtils.getBaseScore(ability);
        const statId = AbilityUtils.getId(ability);
        const statKey = AbilityUtils.getStatKey(ability);
        const totalScore = AbilityUtils.getTotalScore(ability);
        const racialBonus = AbilityUtils.getRacialBonus(ability);
        const classBonuses = AbilityUtils.getClassBonuses(ability);
        const miscBonus = AbilityUtils.getMiscBonus(ability);
        const stackingBonus = AbilityUtils.getStackingBonus(ability);
        const setScore = AbilityUtils.getSetScore(ability);

        let classNames: Array<string> = [
            className,
            'ddbc-ability-score-manager',
            `ddbc-ability-score-manager--${statKey}`,
        ];

        return (
            <div className={classNames.join(' ')}>
                {showHeader && (
                    <div className="ddbc-ability-score-manager__header">
                        <AbilityIcon
                            statId={statId}
                            themeMode="light"
                            className="ddbc-ability-score-manager__header-icon"
                        />
                        <div className="ddbc-ability-score-manager__header-name">{label}</div>
                    </div>
                )}
                <div className="ddbc-ability-score-manager__components">
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Total Score</div>
                        <div className="ddbc-ability-score-manager__component-value">
                            {totalScore === null ? '--' : totalScore}
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Modifier</div>
                        <div className="ddbc-ability-score-manager__component-value">
                            {modifier === null ? '--' : <SignedNumber number={modifier} />}
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Base Score</div>
                        <div className="ddbc-ability-score-manager__component-value">
                            {baseScore === null ? '--' : baseScore}
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Racial Bonus</div>
                        <div className="ddbc-ability-score-manager__component-value">
                            <SignedNumber number={racialBonus} />
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Ability Improvements</div>
                        <div className="ddbc-ability-score-manager__component-value">
                            <SignedNumber number={classBonuses} />
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Misc Bonus</div>
                        <div className="ddbc-ability-score-manager__component-value">
                            <SignedNumber number={miscBonus} />
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Stacking Bonus</div>
                        <div className="ddbc-ability-score-manager__component-value">
                            <SignedNumber number={stackingBonus} />
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__component">
                        <div className="ddbc-ability-score-manager__component-label">Set Score</div>
                        <div className="ddbc-ability-score-manager__component-value">{setScore}</div>
                    </div>
                </div>
                <div className="ddbc-ability-score-manager__overrides">
                    <div className="ddbc-ability-score-manager__override">
                        <div className="ddbc-ability-score-manager__override-label">Other Modifier</div>
                        <div className="ddbc-ability-score-manager__override-value">
                            <input
                                type="number"
                                value={otherBonus === null ? '' : otherBonus}
                                placeholder="--"
                                onChange={this.handleOtherBonusChange}
                                onBlur={this.handleOtherBonusBlur}
                                readOnly={isReadonly}
                            />
                        </div>
                    </div>
                    <div className="ddbc-ability-score-manager__override">
                        <div className="ddbc-ability-score-manager__override-label">Override Score</div>
                        <div className="ddbc-ability-score-manager__override-value">
                            <input
                                type="number"
                                value={overrideScore === null ? '' : overrideScore}
                                placeholder="--"
                                onChange={this.handleOverrideScoreChange}
                                onBlur={this.handleOverrideScoreBlur}
                                readOnly={isReadonly}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
